import * as React from 'react';
import Icon from '@cx-web/components/primitives/icon';
import { type BetslipProviderData, useBetslipContext } from '@cx-web/feature-betslip/context';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES, TRACKING_EVENTS, trackingActions } from '@cx-web/feature-tracking';
import classNames from 'classnames';

import styles from '../style/mini-betslip.module.css';

interface WithContext {
  betslipContext: BetslipProviderData;
}

interface IMiniBetslipProps extends WithContext {
  betslipTitle: string;
  className?: string;
}

interface IMiniBetslipState {
  canRenderBetCount: boolean;
}

// Header toggle that opens/closes the betslip drawer. Exposes aria-expanded
// for state and uses `betslipTitle` as its accessible name. Full ARIA
// navigation contract for tests/AT lives at the top of ../betslip.tsx.
class MiniBetslip extends React.Component<IMiniBetslipProps, IMiniBetslipState> {
  constructor(props: IMiniBetslipProps) {
    super(props);

    this.state = {
      canRenderBetCount: false,
    };
  }

  public componentDidMount(): void {
    this.setState({ canRenderBetCount: true });
  }

  public shouldComponentUpdate(nextProps: IMiniBetslipProps, nextState: IMiniBetslipState) {
    const { canRenderBetCount } = this.state;
    const { canRenderBetCount: nextCanRenderBetCount } = nextState;
    const { added, betslip, count, open } = this.props.betslipContext;
    const { added: nextAdded, betslip: nextBetslip, count: nextCount, open: nextOpen } = nextProps.betslipContext;

    return (
      nextCanRenderBetCount !== canRenderBetCount ||
      nextBetslip !== betslip ||
      nextCount !== count ||
      nextAdded !== added ||
      nextOpen !== open
    );
  }

  private handleBetslipToggle = () => {
    const { betslipContext } = this.props;
    const { toggleBetslip } = betslipContext;

    trackingActions.eventTracking({
      event: TRACKING_EVENTS.BETSLIP_ICON_CLICKED,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BETSLIP_ICON_CLICKED],
    });

    const { betslip, stake } = betslipContext;
    toggleBetslip();
    betslipContext.trackBetSlip(betslip, stake);
  };

  public componentDidUpdate(prevProps: IMiniBetslipProps) {
    const { added: betAdded, count, toggleAdded } = this.props.betslipContext;
    const { count: prevCount } = prevProps.betslipContext;

    if (count > prevCount) {
      toggleAdded(true);
    }

    if (betAdded) {
      setTimeout(() => {
        toggleAdded(false);
      }, 400);
    }
  }

  private renderBetCount(): JSX.Element | null {
    const { canRenderBetCount } = this.state;
    const { added: betAdded, count: betCount } = this.props.betslipContext;
    if (!canRenderBetCount || !betCount) {
      return null;
    }

    return <span className={[styles.betCount, betAdded ? styles.betAdded : ''].join(' ')}>{betCount}</span>;
  }

  public render(): JSX.Element {
    const { betslipTitle, className } = this.props;
    const { count, open } = this.props.betslipContext;
    const accessibleName = count > 0 ? `${betslipTitle}, ${count} ${count === 1 ? 'bet' : 'bets'}` : betslipTitle;

    return (
      <div
        aria-expanded={open}
        aria-label={accessibleName}
        className={styles.wrapper}
        data-testid="betslip-button"
        onClick={this.handleBetslipToggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleBetslipToggle();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className={classNames(styles.miniBetslip, className)}>
          <Icon height={20} icon="Betslip" width={20} />
          {this.renderBetCount()}
        </div>
        <span className={styles.betslipText}>{betslipTitle}</span>
      </div>
    );
  }
}

export function withContext<T extends WithContext = WithContext>(Component: React.ComponentType<T>) {
  const WrappedComponent = (props: Omit<T, keyof WithContext>) => {
    const betslipContext = useBetslipContext();

    return <Component {...(props as T)} betslipContext={betslipContext} />;
  };

  const displayName = Component.displayName || Component.name || 'Betslip';
  WrappedComponent.displayName = `withContext(${displayName})`;

  return WrappedComponent;
}

const MiniBetslipWithContextAndStoreSlices = withContext(MiniBetslip);

export default MiniBetslipWithContextAndStoreSlices;
